import React from "react"
import PropTypes from "prop-types"
import Icon from '../app_components/Icon'
import ProfileLanguageFlag from '../app_components/ProfileLanguageFlag'
import SendConnectRequest from '../connect_sections/SendConnectRequest'
import AlertModal from '../app_components/AlertModal'
import { semantic } from '@lexody/design-tokens'
import {
  resolveAvatarUrl,
  formatLocation,
  getActivityBadgeLabel,
  getLanguageBadgeEntries,
  truncateText,
  formatActivityLabel,
} from '../connect_sections/connectMemberHelpers'

const MAX_LANGUAGE_BADGES = 4

function LanguageBadge({ entry }) {
  if (!entry.flagHtml) return null

  return (
    <span className={`connectMemberCard__languageBadge connectMemberCard__languageBadge--level${entry.level}`}>
      <ProfileLanguageFlag flagHtml={entry.flagHtml} size={18} />
      <span className="connectMemberCard__languageBadgeText">{entry.label}</span>
    </span>
  )
}

class ProfileTeaser extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      connectOpen: false,
      requestSent: false,
      needsPictureModalOpen: false,
    }

    this.openConnect = this.openConnect.bind(this)
    this.closeConnect = this.closeConnect.bind(this)
    this.onRequestSent = this.onRequestSent.bind(this)
    this.goToProfile = this.goToProfile.bind(this)
    this.closePictureModal = this.closePictureModal.bind(this)
  }

  goToProfile() {
    window.location.assign(`/profile/${this.props.match.slug}`)
  }

  openConnect(event) {
    if (event && event.stopPropagation) {
      event.stopPropagation()
    }

    if (this.props.needsPicture) {
      this.setState({ needsPictureModalOpen: true })
      return
    }

    this.setState({ connectOpen: true })
  }

  closeConnect() {
    this.setState({ connectOpen: false })
  }

  closePictureModal() {
    this.setState({ needsPictureModalOpen: false })
  }

  onRequestSent() {
    this.setState({
      connectOpen: false,
      requestSent: true,
    })
  }

  render() {
    const { match, missingImg, premiumUser } = this.props
    const activityLabel = premiumUser ? formatActivityLabel(getActivityBadgeLabel(match.last_seen)) : null
    const location = formatLocation(match.location)
    const languageBadges = getLanguageBadgeEntries(match.languages_users)
    const visibleBadges = languageBadges.slice(0, MAX_LANGUAGE_BADGES)
    const overflowCount = languageBadges.length - MAX_LANGUAGE_BADGES
    const bio = truncateText(match.bio, 150)

    return (
      <>
      <article
        className="friendMatchCard friendMatchCard--clickable"
        onClick={this.goToProfile}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            this.goToProfile()
          }
        }}
        role="link"
        tabIndex={0}
        aria-label={`View ${match.preferred_name}'s profile`}
      >
        <div className="friendMatchCard__photoWrap">
          <img
            className="friendMatchCard__photo"
            src={resolveAvatarUrl(match.avatar_url, missingImg)}
            alt=""
          />

          {activityLabel && (
            <div className="friendMatchCard__lastSeen profile-hero__lastSeen profile-hero__lastSeen--active">
              <span className="profile-hero__lastSeenDot" aria-hidden="true" />
              <span>{activityLabel}</span>
            </div>
          )}

          <svg
            className="friendMatchCard__wave"
            viewBox="0 0 320 28"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,18 C50,30 110,6 160,20 C210,34 270,8 320,18 L320,28 L0,28 Z"
              fill="var(--lex-grey-surface-default, #F3F7F9)"
            />
          </svg>
        </div>

        <div className="friendMatchCard__body">
          <p className="friendMatchCard__name">{match.preferred_name}</p>

          {location && (
            <p className="friendMatchCard__location">
              <Icon name="map-marker" size={12} color={semantic.greyscale.text.subtitle} />
              <span>{location}</span>
            </p>
          )}

          {visibleBadges.length > 0 && (
            <div className="connectMemberCard__languageBadges friendMatchCard__languageBadges">
              {visibleBadges.map((entry) => (
                <LanguageBadge key={entry.key} entry={entry} />
              ))}
              {overflowCount > 0 && (
                <span className="connectMemberCard__languageOverflow">
                  +{overflowCount} more
                </span>
              )}
            </div>
          )}

          {bio && (
            <p className="friendMatchCard__bio">{bio}</p>
          )}

          {this.state.requestSent ? (
            <p className="friendMatchCard__sent">Connect request sent</p>
          ) : (
            <button
              type="button"
              className="friendMatchCard__connectBtn"
              onClick={this.openConnect}
              aria-label={`Connect with ${match.preferred_name}`}
            >
              <Icon name="user-plus" size={22} color={semantic.primary.surface.default} />
            </button>
          )}

          <div className="friendMatchCard__bodyWave" aria-hidden="true" />
        </div>
      </article>

      {this.state.needsPictureModalOpen && (
        <AlertModal
          onClose={this.closePictureModal}
          bannerText="Opps! Where is your picture?"
          subHeaderText="You need to upload a picture of your face."
          mainText="In order to contact other members, you need to complete your profile. Upload a picture first, and then you can start Connecting!"
          onClick={() => window.location.assign('/edit')}
          buttonText="Upload Picture"
        />
      )}

      {this.state.connectOpen && (
        <SendConnectRequest
          requestee_id={match.id}
          requestee_name={match.preferred_name}
          closeRequest={this.closeConnect}
          requestSent={this.onRequestSent}
          lexicoins={this.props.lexicoins}
          og_connection={false}
          pro={this.props.premiumUser}
        />
      )}
      </>
    )
  }
}

ProfileTeaser.propTypes = {
  match: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    avatar_url: PropTypes.string,
    preferred_name: PropTypes.string,
    location: PropTypes.string,
    last_seen: PropTypes.string,
    bio: PropTypes.string,
    languages_users: PropTypes.array,
  }).isRequired,
  missingImg: PropTypes.string.isRequired,
  lexicoins: PropTypes.number,
  premiumUser: PropTypes.bool,
  needsPicture: PropTypes.bool,
}

ProfileTeaser.defaultProps = {
  lexicoins: 0,
  premiumUser: false,
  needsPicture: false,
}

export default ProfileTeaser
